import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpRequest, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { IImagesModel } from '../models/image.model';
import { NgForm } from '@angular/forms';
import { IUserModel, IUser, ICreditModel } from '../models/user.model';
import { apiParameters } from 'src/app/configs/api.params';
import { ErrorService } from './error.service';
import { IError } from '../models/error.model';
import { StorageService } from './storage/storage.service';


@Injectable({
  providedIn: 'root'
})
export class UserService {

  private token: string;
  private user: any;
  public apiEndPoint = apiParameters.url;
  // public apiParam = '/users/7';

  constructor(
    private httpClient: HttpClient,
    private errorService: ErrorService,
    private storage: StorageService
  ) {}

  get myToken(): string {
    return this.token;
  }
  set myToken(helper: string) {
    this.token = helper;
  }

  /**
   * Determines whether user loggedin is
   * @returns true if user loggedin 
   */
  isUserLoggedin(): boolean {
    const user = sessionStorage.getItem('currentUser');
    if (user) {
      this.user = sessionStorage.getItem('currentUser');
      return true;
    }
    return false;
  }

  /**
   * Gets logged in user
   * @returns logged in user 
   */
  getLoggedInUser(): IUserModel {
    return this.storage.getSessionObject('currentUser') || this.storage.getLocalObject('currentUser');
  }

  updateCurrentUser(user: IUser) {
    const currentUser = this.getLoggedInUser();
    const token = currentUser.token;
    sessionStorage.setItem('currentUser', JSON.stringify({ user, token }));
  }

  login(userEmail: string, userPassword: string): Observable<IUserModel> {
    return this.httpClient.post(apiParameters.url + '/login', { email: userEmail, password: userPassword }, {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
    })
      .pipe(
        map((res: IUserModel) => {
          const token = res.token;
          const user = res.user;
          if (token) {
            if (user.role.name === 'manager' || user.role.name === 'user' || user.role.name === 'root' || user.role.name === 'customer') {
              sessionStorage.setItem('currentUser', JSON.stringify({ user, token }));
              this.user = user;
              this.token = token;
            }
            return res;
          }
          return null;
        }, (err: HttpErrorResponse) => {
          return JSON.parse(JSON.stringify(err));
        })
      );
  }

  get(): Observable<IUser> {
    const currentUser = this.getLoggedInUser();
    const token = currentUser.token;
    const userId = currentUser.user.id;
    const httpOptions = {
      headers: new HttpHeaders({
      })
    };
    const body: any = {};
    return this.httpClient.get(apiParameters.url + '/users/' + userId, httpOptions)
      .pipe(map((res: IUser) => {
        return res;
      })
      );
  }

  getById(id: string): Observable<IUser> {
    const httpOptions = {
      headers: new HttpHeaders({
      })
    };
    return this.httpClient.get(apiParameters.url + '/users/' + id, httpOptions)
      .pipe(map((res: IUser) => {
        return res;
      })
      );
  }

  /**
   * Transfers credit to user from client
   * @param userId - user id
   * @param amount - amount of credit
   * @param fromClientId - client id
   * @returns credit 
   */
  transferCredit(userId: string, amount: number, fromClientId?: string): Observable<ICreditModel> {
    const httpOptions = {
      headers: new HttpHeaders({
      })
    };

    let body = {
      amount
    }

    if (fromClientId) {
      body['fromClientId'] = fromClientId;
    }

    return this.httpClient.post(apiParameters.url + '/users/' + userId + '/transfer-credits', body, httpOptions)
      .pipe(
        map((res: ICreditModel) => {
          return res;
        })
      );
  }

  private handleError(errorRes: HttpErrorResponse) {
    let error = {} as IError;
    error = this.errorService.whichError(errorRes);
    return throwError(error);
  }

  subCredit(userId: string, total: number): Observable<ICreditModel> {
    const httpOptions = {
      headers: new HttpHeaders({


      })
    };

    return this.httpClient.post(apiParameters.url + '/users/' + userId + '/remove-credit', { total }, httpOptions)
      .pipe(map((res: ICreditModel) => {
        return res;
      })
      );
  }

  changeApplication(userId: string, clientId: string): Observable<IUser> {
    const httpOptions = {
      headers: new HttpHeaders({


      })
    };

    return this.httpClient.patch(apiParameters.url + '/users/' + userId + '/transfer', { targetClientId: clientId }, httpOptions)
      .pipe(map((res: IUser) => {
        return res;
      })
      );
  }

  signup(userName: string, address: string, uuid: string, userEmail: string, userPassword: string): Observable<IUser> {
    return this.httpClient.post(apiParameters.url + '/users/sign-up',
      { name: userName, address, uid: uuid, email: userEmail, password: userPassword },
      { headers: new HttpHeaders().set('Content-Type', 'application/json') }
    )
      .pipe(map((res: IUser) => {
        return res;
      }));
  }
  reset(userEmail: string): Observable<IUser> {
    return this.httpClient.post(apiParameters.url + '/password/forgot',
      { email: userEmail },
      { headers: new HttpHeaders().set('Content-Type', 'application/json') }
    )
      .pipe(map((res: IUser) => {
        return res;
      }));
  }

  logout() {
    // clear token remove user from local storage to log user out
    this.token = null;
    sessionStorage.removeItem('currentUser');
    // localStorage.removeItem('currentUser');
  }

  patch(form: NgForm): Observable<IUser> {
    const email = form.form.value.email;
    const name = form.form.value.name;
    // const uid = form.form.value.uid;

    const currentUser = this.getLoggedInUser();
    const token = currentUser.token;
    const userId = currentUser.user.id;
    const httpOptions = {
      headers: new HttpHeaders({


      })
    };
    let body: any;

    body = {};
    if (email !== currentUser.user.email) {
      body.email = email;
    }
    if (name !== currentUser.user.name) {
      body.name = name === '' ? null : name;
    }

    return this.httpClient.patch(apiParameters.url + '/users/' + userId, body, httpOptions)
      .pipe(map((res: IUser) => {
        this.updateCurrentUser(res);
        return res;
      })
      );
  }

  addFile(formData: FormData, userId: string, type?: string): Observable<any> {
    let typeText = '';
    if (type && type !== '') {
      typeText = '?type=' + type;
    }
    const httpOptions = {
      // reportProgress: true,
      headers: new HttpHeaders({

      })
    };
    const url = apiParameters.url + '/users/' + userId + '/files/' + typeText;
    return this.httpClient.post(url, formData, httpOptions);
    // return this.httpClient.request(new HttpRequest('POST', url, formData, httpOptions));
  }

  addFileAsRoot(formData: FormData, userId: string, type?: string) {
    let typeText = '';
    if (type && type !== '') {
      typeText = '?type=' + type;
    }

    const httpOptions = {
      reportProgress: true,
      headers: new HttpHeaders({

      })
    };
    const url = apiParameters.url + '/users/' + userId + '/files' + typeText;
    return this.httpClient.request(new HttpRequest('POST', url, formData, httpOptions));
  }

  getFiles(userId: string, type?: string): Observable<IImagesModel[]> {
    let typeText = '';
    if (type && type !== '') {
      typeText = '?type=' + type;
    }

    const httpOptions = {
      headers: new HttpHeaders({

      })
    };
    const url = apiParameters.url + '/users/' + userId + '/files' + typeText;
    return this.httpClient.get(url, httpOptions)
      .pipe(map((res: IImagesModel[]) => {
        return res;
      })
      );
  }

  deleteFile(userId: string, fileId: string): Observable<any> {
    const url = apiParameters.url + '/users/' + userId + '/files/' + fileId;
    return this.httpClient.delete(url);
  }

  deleteFileAsRoot(userId: string, fileId: string): Observable<any> {
    const url = apiParameters.url + '/users/' + userId + '/files/' + fileId;
    return this.httpClient.delete(url);
  }

  deleteUser(userId: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({

      })
    };

    const url = apiParameters.url + '/users/' + userId;
    return this.httpClient.delete(url, httpOptions);
  }

  patchUser(body, userId: string): Observable<any> {
    return this.httpClient.patch(`${apiParameters.url}/users/${userId}`, body)
    .pipe(map((res: IUser) => {
      return res;
    })
    );
  }

  patchUserMaxCount(userId: string, maxCount: number): Observable<IUser> {
    const httpOptions = {
      headers: new HttpHeaders({
      })
    };
    let body: any;

    body = {};
    if (maxCount) {
      body.maxCount = maxCount;
    }

    return this.httpClient.patch(apiParameters.url + '/users/' + userId, body, httpOptions)
      .pipe(map((res: IUser) => {
        return res;
      })
      );
  }

  /**
   * Edit password
   * @param form - ngForm with field old password, new password, password repeat
   * @returns  
   */
  patchPassword(form: NgForm) {
    const oldPassword = form.form.value.passwordOld;
    const newPassword = form.form.value.password;
    const newPasswordRepeat = form.form.value.passwordRepeat;

    const currentUser = this.getLoggedInUser();
    const token = currentUser.token;
    const userId = currentUser.user.id;
    const httpOptions = {
      headers: new HttpHeaders({


      })
    };

    const body = {
      passwordOld: oldPassword,
      password: newPassword,
      passwordRepeat: newPasswordRepeat,
    };

    return this.httpClient.patch(apiParameters.url + '/users/' + userId + '/change-password', body, httpOptions)
      .pipe(map((res: IUser) => {
        return res;
      })
      );

  }

  changePassword(form: NgForm, id: string) {
    // const oldPassword = form.form.value.passwordOld;
    const newPassword = form.form.value.password;
    const newPasswordRepeat = form.form.value.repeatPassword;

    const httpOptions = {
      headers: new HttpHeaders({


      })
    };

    const body = {
      // "passwordOld" : oldPassword,
      password: newPassword,
      passwordRepeat: newPasswordRepeat,
    };

    return this.httpClient.patch(apiParameters.url + '/users/' + id + '/change-password', body, httpOptions)
      .pipe(map((res: IUser) => {
        return res;
      })
      );

  }

  getUserRoleName(): string {
    return JSON.parse((sessionStorage.getItem('currentUser'))).user.role.name;
  }

  getUserRoleType(): string {
    return JSON.parse((sessionStorage.getItem('currentUser'))).user.role.type;

  }


  /* Root methods */

  // Method for add new user

  /**
   * Adds new user as root
   * @param body - user data
   * @param clientId - client id
   * @returns User 
   */
  addUser(body, clientId): Observable<IUser> {

    return this.httpClient.post(apiParameters.url + '/clients/' + clientId + '/users', body)
      .pipe(map((res: IUser) => {
        return res;
      })
      );
  }


  /**
   * Gets user files as root
   * @param userId - user id
   * @param type - type e.g {logo, background}
   * @returns User files as root 
   */
  getUserFilesAsRoot(userId: string, type: string): Observable<IImagesModel[]> {
    let typeText = '';
    if (type && type !== '') {
      typeText = '?type=' + type;
    }
    const httpOptions = {
      headers: new HttpHeaders({

      })
    };
    const url = apiParameters.url + '/users/' + userId + '/files' + typeText;
    return this.httpClient.get(url, httpOptions)
      .pipe(map((res: IImagesModel[]) => {
        return res;
      })
      );
  }

  addUserFilesAsRoot(formData: FormData, userId: string, type?: string) {
    let typeText = '';
    if (type && type !== '') {
      typeText = '?type=' + type;
    }
    const currentUser = this.getLoggedInUser();
    const token = currentUser.token;
    const rootId = currentUser.user.id;
    const httpOptions = {
      reportProgress: true,
      headers: new HttpHeaders({

      })
    };
    const url = apiParameters.url + '/root/' + rootId + '/users/' + userId + '/files/' + typeText;
    return this.httpClient.request(new HttpRequest('POST', url, formData, httpOptions));
  }

  // Method for get all users

  /**
   * Gets all users as root
   * @returns List of users 
   */
  getUsersAsRoot(): Observable<IUser[]> {
    const httpOptions = {
      headers: new HttpHeaders({

      })
    };
    return this.httpClient.get(apiParameters.url + '/users', httpOptions)
      .pipe(
        map((res: IUser[]) => {
          return res;
        })
      );
  }

  getByApplicationId(applicationId: string): Observable<IUser[]> {
    return this.httpClient.get(apiParameters.url + '/clients/' + applicationId + '/users')
      .pipe(
        map((res: IUser[]) => {
          const result = res.filter(user => user.role.name === 'user');
          return result;
        }),
        map((res: IUser[]) => {
          res.sort((a, b) => {
            return (new Date(b.lastTimeOnline) as any) - (new Date(a.lastTimeOnline) as any);
          });
          return res;
        })
      );
  }

  patchLicensePlate(userId: string, status: boolean): Observable<IUser> {
    let body: any;
    body = {
      licensePlateEnabled: status
    };

    return this.httpClient.patch(apiParameters.url + '/users/' + userId, body)
      .pipe(map((res: IUser) => {
        return res;
      })
      );
  }

  patchReflectiveFloor(userId: string, status: boolean): Observable<IUser> {
    let body: any;
    body = {
      reflectiveFloorEnabled: status
    };

    return this.httpClient.patch(apiParameters.url + '/users/' + userId, body)
      .pipe(map((res: IUser) => {
        return res;
      })
      );
  }

  getUsers(page: number, limit: number, name: string, offlineDays: number, metaSearch?: any, orderBy?: string, direction?: string): Observable<any> {
    let searchParams = new HttpParams();

    if (name && name !== '') {
      searchParams = searchParams.append('search', name);
    }

    if (orderBy && orderBy !== '') {
      searchParams = searchParams.append('orderBy', orderBy);
    }

    if (direction && direction !== '') {
      searchParams = searchParams.append('orderDirection', direction);
    }

    if (metaSearch && metaSearch.length) {
      metaSearch.forEach(field => {
        if (field.fieldKey !== '' && field.fieldValue !== '') {
          searchParams = searchParams.append(field.fieldKey, field.fieldValue);
        }
      });
    }
    if (offlineDays) {
      const date = new Date();
      date.setDate(date.getDate() - offlineDays);
      searchParams = searchParams.append('onlineBefore', date.toISOString().split('T')[0]);
    }

    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString()
      }),
      params: searchParams
    };

    return this.httpClient.get(apiParameters.url + '/users', httpOptions)
      .pipe(
        map((res: any) => {
          res.results = res.results.filter(user => !(user.role.name === 'root' || user.role.name === 'manager' || user.role.name === "qcmanager"));
          return res;
        })
      );
  }

  getUserSettings(userId: string, profileId?: string): Observable<any> {

    let searchParams = new HttpParams();

    if (profileId && profileId !== '') {
      searchParams = searchParams.append('profileId', profileId);
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
      params: searchParams
    };

    return this.httpClient.get(apiParameters.url + '/users/' + userId + '/settings', httpOptions)
      .pipe(map((res: any) => {
        return res;
      })
      );
  }

  patchUserSettings(userId: string, body, settingsId?: string): Observable<any> {

    let searchParams = new HttpParams();

    if (settingsId && settingsId !== '') {
      searchParams = searchParams.append('profileId', settingsId);
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
      params: searchParams
    };

    return this.httpClient.patch(apiParameters.url + '/users/' + userId + '/settings', body, httpOptions)
      .pipe(map((res: any) => {
        return res;
      })
      );
  }

  getLicensePlates(userId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/users/${userId}/license-plates`);
  }

  addLicensePlate(userId: string, body): Observable<any> {
    return this.httpClient.post(`${apiParameters.url}/users/${userId}/license-plates`, body);
  }

  removeLicensePlate(userId: string, plateId: string): Observable<any> {
    return this.httpClient.delete(`${apiParameters.url}/users/${userId}/license-plates/${plateId}`);
  }

  getDevices(): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/devices`);
  }

  subscribeDevice(userId: string, body): Observable<any> {
    return this.httpClient.post(`${apiParameters.url}/devices`, body);
  }

  createSettingsProfile(userId: string, body): Observable<any> {
    return this.httpClient.post(`${apiParameters.url}/users/${userId}/profiles`, body);
  }

  getSettingsProfiles(userId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/users/${userId}/profiles`);
  }

  updateSettingsProfile(userId: string, profileId: string, body): Observable<any> {
    return this.httpClient.patch(`${apiParameters.url}/users/${userId}/profiles/${profileId}`, body);
  }

  deleteSettingsProfile(userId: string, profileId: string): Observable<any> {
    return this.httpClient.delete(`${apiParameters.url}/users/${userId}/profiles/${profileId}`);
  }

  getVinSettings(userId: string, settingsId: string): Observable<any> {
    let searchParams = new HttpParams();

    if (settingsId && settingsId !== '') {
      searchParams = searchParams.append('profileId', settingsId);
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
      params: searchParams
    };

    return this.httpClient.get(`${apiParameters.url}/users/${userId}/settings`, httpOptions);
  }

  updateVinSettings(userId: string, settingsId: string, body): Observable<any> {
    let searchParams = new HttpParams();

    if (settingsId && settingsId !== '') {
      searchParams = searchParams.append('profileId', settingsId);
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
      params: searchParams
    };

    return this.httpClient.patch(`${apiParameters.url}/users/${userId}/settings`, body, httpOptions);
  }

  getUserDevices(userId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/users/${userId}/devices`);
  }

  getCreditsHistory(userId: string, page: number, limit: number): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString()
      })
    };

    return this.httpClient.get(`${apiParameters.url}/users/${userId}/credits-history`, httpOptions);
  }

  createInvoice(userId: string): Observable<any> {
    return this.httpClient.post(`${apiParameters.url}/users/${userId}/invoices`, {});
  }

  getInvoices(userId: string, page: number, limit: number): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString()
      })
    };

    return this.httpClient.get(`${apiParameters.url}/users/${userId}/invoices`, httpOptions)
      .pipe(
        map((res: any) => {
          res.results.sort((a, b) => {
            return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
          });
          return res;
        })
      );
  }

  getInvoiceById(userId: string, invoiceId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/users/${userId}/invoices/${invoiceId}`);
  }

  closeInvoice(userId: string, invoiceId: string): Observable<any> {
    return this.httpClient.patch(`${apiParameters.url}/users/${userId}/invoices/${invoiceId}/close`, {});
  }

  generateCSV(userId: string, invoiceId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/users/${userId}/invoices/${invoiceId}/csv`);
  }

  getInvoiceItems(userId: string, invoiceId: string, page: number, limit: number): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString()
      })
    };

    return this.httpClient.get(`${apiParameters.url}/users/${userId}/invoices/${invoiceId}/items`, httpOptions);
  }

  getUerVehicleTypes(userId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.urlV2}/users/${userId}/vehicle-types`);
  }

  getDeletedUsers(page: number, limit: number, name: string): Observable<any> {
    let searchParams = new HttpParams();

    if (name && name !== '') {
      searchParams = searchParams.append('search', name);
    }

    searchParams = searchParams.append('onlyDeleted', 'true');
    searchParams = searchParams.append('orderBy', 'createdAt');
    searchParams = searchParams.append('orderDirection', 'DESC NULLS LAST');

    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString()
      }),
      params: searchParams
    };

    return this.httpClient.get(apiParameters.url + '/users', httpOptions);
  }

  revertUsers(body): Observable<any> {
    return this.httpClient.post(`${apiParameters.url}/revert-user`, body);
  }
}

